import React from 'react';
import { Grid, LinearProgress } from '@mui/material';

// ============================================================================
// INDICADOR DE PROGRESSO DA ANÁLISE DE JABUTI
// ----------------------------------------------------------------------------
// Nasceu de uma reclamação concreta do cliente: a tela mostrava "IA em
// processamento" parado e não havia como distinguir "rodando" de "travado".
//
// Dois sinais, porque um só não basta:
//   - o CRONÔMETRO responde "quanto tempo já faz" — mas continua correndo mesmo
//     se o worker morreu, então sozinho ele mente;
//   - a ETAPA responde "está vivo" — ela só avança se algum worker tocou no
//     registro agora há pouco.
//
// E um terceiro estado, o que faltava por completo: EXPIROU. Passado o limite,
// a tela para de girar e diz o que de fato sabe — que a análise saiu do campo
// de visão da tela, não que morreu. É honesto e devolve o controle ao usuário.
// ============================================================================

// Teto do acompanhamento pela tela. Uma análise típica leva de 1 a 3 minutos
// (4 etapas, sendo 3 chamadas à OpenAI); 5 min dá folga confortável sobre isso
// sem deixar o usuário preso a um indicador que não vai mais mudar.
export const LIMITE_ACOMPANHAMENTO_MS = 5 * 60 * 1000;

// Frequência do polling de status. Não vale apertar: a análise leva minutos,
// e cada tick é uma consulta por item em tela.
export const INTERVALO_POLLING_MS = 5000;

export const formatarDecorrido = (ms: number): string => {
    const total = Math.max(0, Math.floor(ms / 1000));
    const min = Math.floor(total / 60);
    const seg = total % 60;
    return min > 0 ? `${min}min ${String(seg).padStart(2, '0')}s` : `${seg}s`;
};

interface IProgressoJabuti {
    /** Etapa corrente (1..4). 0 = ainda preparando/aguardando transcrição. */
    etapa?: number;
    totalEtapas?: number;
    /** Rótulo da etapa, vindo do back para não sair de sincronia com o pipeline. */
    rotulo?: string;
    decorridoMs: number;
    expirou?: boolean;
}

export const ProgressoJabuti: React.FC<IProgressoJabuti> = ({
    etapa = 0,
    totalEtapas = 4,
    rotulo,
    decorridoMs,
    expirou = false,
}) => {
    if (expirou) {
        return (
            <Grid
                item
                style={{
                    fontSize: '12px',
                    color: '#8a6d3b',
                    fontWeight: 600,
                    fontStyle: 'italic',
                    maxWidth: '260px',
                    lineHeight: 1.4,
                }}
            >
                A análise está demorando mais que o normal. Ela continua rodando em segundo
                plano — o resultado aparece aqui assim que terminar.
            </Grid>
        );
    }

    // etapa 0 é um estado real (preparando, ou esperando o inteiro teor ser
    // transcrito), não um erro — por isso a contagem "X de 4" só aparece a
    // partir da 1, para não exibir um enganoso "Etapa 0 de 4".
    const mostrarContagem = etapa >= 1;
    const proporcao = mostrarContagem ? (etapa / totalEtapas) * 100 : 0;

    return (
        <Grid item style={{ minWidth: '190px', maxWidth: '260px' }}>
            <Grid style={{ fontSize: '12px', color: 'red', fontWeight: 900, fontStyle: 'italic' }}>
                IA em processamento
            </Grid>
            <Grid style={{ fontSize: '11px', color: '#555', fontStyle: 'italic', marginTop: '2px' }}>
                {mostrarContagem
                    ? `Etapa ${etapa} de ${totalEtapas}${rotulo ? ` — ${rotulo}` : ''}`
                    : (rotulo || 'Preparando')}
                {' · '}
                {formatarDecorrido(decorridoMs)}
            </Grid>
            <LinearProgress
                variant={mostrarContagem ? 'determinate' : 'indeterminate'}
                value={proporcao}
                style={{ marginTop: '6px', height: '4px', borderRadius: '2px' }}
            />
        </Grid>
    );
};

export default ProgressoJabuti;
